import { Injectable, computed, inject, signal } from '@angular/core';
import { HttpClient, HttpParams } from '@angular/common/http';
import { environment } from '../../environments/environment';
import { forkJoin, Observable, of } from 'rxjs';
import { catchError, map, tap } from 'rxjs/operators';

export interface Novelty {
  id: number;
  scope?: 'business' | 'platform';
  type: string;
  category: string;
  title: string;
  summary: string;
  description?: string | null;
  priority: string;
  status: string;
  created_at: string;
  target_path?: string | null;
  action_label?: string | null;
  reservation_id?: number | null;
  business_id?: number | null;
  space_id?: number | null;
  payment_id?: number | null;
  subscription_id?: number | null;
  owner_id?: number | null;
  reason?: string | null;
  metadata?: Record<string, unknown> | null;
  email_status?: string;
  isPreview?: boolean;
  expires_at?: string | null;
  read_at?: string | null;
}

export interface NoveltyListResponse {
  data: Novelty[];
  total: number;
  page: number;
  pages: number;
  limit: number;
}

export interface NoveltyMeta {
  categories: readonly string[];
  priorities: readonly string[];
  statuses: readonly string[];
  scopes: readonly string[];
  types: { type: string; category: string; defaultPriority: string; defaultTitle: string; defaultSummary: string; ttlDays: number | null; notifyEmail: boolean }[];
}

export interface BroadcastPayload {
  title: string;
  summary: string;
  description?: string;
  priority?: string;
  category?: string;
  action_label?: string;
  target_path?: string;
}

export interface BroadcastResponse {
  msg: string;
  businesses_notified: number;
  super_admins_notified: number;
  broadcast_id: string;
}

export type NoveltyFilter = Record<string, string | number>;
export interface GlobalUnreadSummary {
  unread: number;
  categories: Record<string, number>;
  modules?: Record<string, number>;
}

@Injectable({ providedIn: 'root' })
export class NoveltyService {
  private readonly url = `${environment.apiUrl}/novelties`;
  private readonly http = inject(HttpClient);

  /** Streams reactivos de unread count: business y global */
  private readonly businessUnreadSignal = signal(0);
  private readonly reservationUnreadSignal = signal(0);
  private readonly customerUnreadSignal = signal(0);
  private readonly globalUnreadSignal = signal(0);
  private readonly globalPaymentUnreadSignal = signal(0);
  private readonly globalOwnerUnreadSignal = signal(0);
  private readonly globalSubscriptionUnreadSignal = signal(0);
  private readonly globalAdministrationUnreadSignal = signal(0);
  private readonly globalModuleUnreadSignal = signal<Record<string, number>>({});

  readonly businessUnread = computed(() => this.businessUnreadSignal());
  readonly reservationUnread = computed(() => this.reservationUnreadSignal());
  readonly customerUnread = computed(() => this.customerUnreadSignal());
  readonly globalUnread = computed(() => this.globalUnreadSignal());
  readonly globalPaymentUnread = computed(() => this.globalPaymentUnreadSignal());
  readonly globalOwnerUnread = computed(() => this.globalOwnerUnreadSignal());
  readonly globalSubscriptionUnread = computed(() => this.globalSubscriptionUnreadSignal());
  readonly globalAdministrationUnread = computed(() => this.globalAdministrationUnreadSignal());

  globalModuleUnread(section: string): number {
    return this.globalModuleUnreadSignal()[section] || 0;
  }

  private businessPollingFilter = signal<{ businessId: number | null } | null>(null);

  /** Carga el contador de novedades business al entrar al módulo (sin polling). */
  startBusinessPolling(businessId: number) {
    this.businessPollingFilter.set({ businessId });
    this.loadBusinessCounts();
  }

  private loadBusinessCounts(): void {
    const activeBusinessId = this.businessPollingFilter()?.businessId || 0;
    if (!activeBusinessId) return;
    forkJoin({
      total: this.unreadCount(activeBusinessId).pipe(catchError(() => of({ unread: this.businessUnreadSignal() }))),
      reservations: this.list({ business_id: activeBusinessId, category: 'reservations', status: 'unread', page: 1, limit: 1 }).pipe(catchError(() => of({ total: this.reservationUnreadSignal() } as NoveltyListResponse))),
      customers: this.list({ business_id: activeBusinessId, category: 'customers', status: 'unread', page: 1, limit: 1 }).pipe(catchError(() => of({ total: this.customerUnreadSignal() } as NoveltyListResponse))),
    }).subscribe(({ total, reservations, customers }) => {
      this.businessUnreadSignal.set(total.unread || 0);
      this.reservationUnreadSignal.set(reservations.total || 0);
      this.customerUnreadSignal.set(customers.total || 0);
    });
  }

  refreshBusinessCount(businessId: number) {
    return this.unreadCount(businessId).pipe(
      map(r => { this.businessUnreadSignal.set(r.unread || 0); return r; }),
    );
  }

  /** Carga el contador de novedades globales al entrar al módulo (sin polling). */
  startGlobalPolling() {
    this.loadGlobalCounts();
  }

  private loadGlobalCounts(): void {
    this.globalUnreadSummary().pipe(
      catchError(() => of({
        unread: this.globalUnreadSignal(),
        categories: {
          payments: this.globalPaymentUnreadSignal(),
          owners: this.globalOwnerUnreadSignal(),
          subscriptions: this.globalSubscriptionUnreadSignal(),
          administration: this.globalAdministrationUnreadSignal(),
        },
        modules: this.globalModuleUnreadSignal(),
      }))
    ).subscribe(summary => {
      this.applyGlobalSummary(summary);
    });
  }

  refreshGlobalCount() {
    return this.globalUnreadSummary().pipe(
      map(r => { this.applyGlobalSummary(r); return r; }),
    );
  }

  refreshGlobalPaymentCount() {
    return this.globalList({ category: 'payments', status: 'unread', page: 1, limit: 1 }).pipe(
      map(r => { this.globalPaymentUnreadSignal.set(r.total || 0); return r; }),
    );
  }

  /** Forzado de actualización local (después de marcar leído sin recargar servicio). */
  decrementBusinessUnread() { this.businessUnreadSignal.update(v => Math.max(0, v - 1)); }
  decrementBusinessCategory(category: string) {
    if (category === 'reservations') this.reservationUnreadSignal.update(v => Math.max(0, v - 1));
    if (category === 'customers') this.customerUnreadSignal.update(v => Math.max(0, v - 1));
  }
  clearBusinessCategoryCounts() {
    this.reservationUnreadSignal.set(0);
    this.customerUnreadSignal.set(0);
  }
  decrementGlobalUnread() { this.globalUnreadSignal.update(v => Math.max(0, v - 1)); }
  // Bug real corregido: novedades-superadmin.component.ts llamaba siempre a
  // refreshGlobalPaymentCount() al leer/resolver UNA novedad global, sin
  // importar su categoría real -- el badge de "Pagos" se refrescaba (casi
  // siempre sin cambios) mientras que los badges reales de "Propietarios" y
  // "Suscripciones" quedaban desfasados hasta la próxima recarga completa.
  decrementGlobalCategory(category: string) {
    if (category === 'payments') this.globalPaymentUnreadSignal.update(v => Math.max(0, v - 1));
    if (category === 'owners') this.globalOwnerUnreadSignal.update(v => Math.max(0, v - 1));
    if (category === 'subscriptions') this.globalSubscriptionUnreadSignal.update(v => Math.max(0, v - 1));
    if (category === 'administration') this.globalAdministrationUnreadSignal.update(v => Math.max(0, v - 1));
  }
  setBusinessUnread(v: number) { this.businessUnreadSignal.set(Math.max(0, v)); }
  setGlobalUnread(v: number) { this.globalUnreadSignal.set(Math.max(0, v)); }

  // ---------------- Endpoints ----------------

  meta(): Observable<NoveltyMeta> { return this.http.get<NoveltyMeta>(`${this.url}/_meta`); }

  list(filters: NoveltyFilter): Observable<NoveltyListResponse> {
    const params = this.buildParams(filters);
    return this.http.get<NoveltyListResponse>(this.url, { params });
  }

  unreadCount(businessId: number): Observable<{ unread: number }> {
    return this.http.get<{ unread: number }>(`${this.url}/unread-count`, { params: { business_id: businessId } });
  }

  markRead(id: number): Observable<unknown> {
    return this.http.patch(`${this.url}/${id}/read`, {});
  }

  markResolved(id: number): Observable<unknown> {
    return this.http.patch(`${this.url}/${id}/resolve`, {});
  }

  markAllRead(businessId: number): Observable<unknown> {
    return this.http.patch(`${this.url}/read-all`, { business_id: businessId }).pipe(tap(()=>{
      this.businessUnreadSignal.set(0);
      this.clearBusinessCategoryCounts();
    }));
  }

  markBusinessCategoryRead(businessId:number,category:'reservations'|'customers'):Observable<unknown>{
    return this.http.patch(`${this.url}/read-category`,{business_id:businessId,category}).pipe(tap(()=>{
      const previous=category==='reservations'?this.reservationUnreadSignal():this.customerUnreadSignal();
      this.businessUnreadSignal.update(value=>Math.max(0,value-previous));
      if(category==='reservations')this.reservationUnreadSignal.set(0);
      if(category==='customers')this.customerUnreadSignal.set(0);
    }));
  }

  // ---------------- Global (super admin) ----------------

  globalList(filters: NoveltyFilter): Observable<NoveltyListResponse> {
    const params = this.buildParams(filters);
    return this.http.get<NoveltyListResponse>(`${this.url}/global`, { params });
  }

  globalUnreadCount(): Observable<{ unread: number }> {
    return this.http.get<{ unread: number }>(`${this.url}/global/unread-count`);
  }

  globalUnreadSummary(): Observable<GlobalUnreadSummary> {
    return this.http.get<GlobalUnreadSummary>(`${this.url}/global/unread-summary`);
  }

  globalMarkRead(id: number): Observable<unknown> {
    return this.http.patch(`${this.url}/global/${id}/read`, {});
  }

  globalMarkResolved(id: number): Observable<unknown> {
    return this.http.patch(`${this.url}/global/${id}/resolve`, {});
  }

  globalMarkAllRead(): Observable<unknown> {
    return this.http.patch(`${this.url}/global/read-all`, {}).pipe(tap(()=>this.clearGlobalCounts()));
  }

  globalMarkCategoryRead(category:'owners'|'subscriptions'|'payments'|'administration'):Observable<unknown>{
    return this.http.patch(`${this.url}/global/read-category`,{category}).pipe(tap(()=>this.clearGlobalCategory(category)));
  }

  globalMarkModuleRead(section: string): Observable<unknown> {
    return this.http.patch(`${this.url}/global/read-module`, { section }).pipe(
      tap(() => this.clearGlobalModule(section)),
    );
  }

  /** Broadcast: envía anuncio a todos los owners (solo super_admin). */
  broadcast(payload: BroadcastPayload): Observable<BroadcastResponse> {
    return this.http.post<BroadcastResponse>(`${this.url}/global/broadcast`, payload);
  }

  private buildParams(filters: NoveltyFilter): HttpParams {
    let params = new HttpParams();
    Object.entries(filters).forEach(([k, v]) => {
      if (v !== '' && v !== null && v !== undefined) params = params.set(k, String(v));
    });
    return params;
  }

  private applyGlobalSummary(summary: GlobalUnreadSummary): void {
    const categories = summary.categories || {};
    const modules = summary.modules || {};
    this.globalUnreadSignal.set(summary.unread || 0);
    this.globalModuleUnreadSignal.set(modules);
    this.globalPaymentUnreadSignal.set(modules['pagosNegocios'] || 0);
    this.globalOwnerUnreadSignal.set(modules['owners'] || 0);
    this.globalSubscriptionUnreadSignal.set(modules['subs'] || 0);
    this.globalAdministrationUnreadSignal.set(categories['administration'] || 0);
  }

  private clearGlobalModule(section: string): void {
    const current = this.globalModuleUnreadSignal()[section] || 0;
    this.globalUnreadSignal.update(value => Math.max(0, value - current));
    this.globalModuleUnreadSignal.update(modules => ({ ...modules, [section]: 0 }));
    if (section === 'pagosNegocios') this.globalPaymentUnreadSignal.set(0);
    if (section === 'owners') this.globalOwnerUnreadSignal.set(0);
    if (section === 'subs') this.globalSubscriptionUnreadSignal.set(0);
  }

  private clearGlobalCategory(category:string):void{
    const current=category==='payments'?this.globalPaymentUnreadSignal():category==='owners'?this.globalOwnerUnreadSignal():category==='subscriptions'?this.globalSubscriptionUnreadSignal():this.globalAdministrationUnreadSignal();
    this.globalUnreadSignal.update(value=>Math.max(0,value-current));
    if(category==='payments')this.globalPaymentUnreadSignal.set(0);
    if(category==='owners')this.globalOwnerUnreadSignal.set(0);
    if(category==='subscriptions')this.globalSubscriptionUnreadSignal.set(0);
    if(category==='administration')this.globalAdministrationUnreadSignal.set(0);
  }

  private clearGlobalCounts():void{
    this.globalUnreadSignal.set(0);
    this.globalPaymentUnreadSignal.set(0);
    this.globalOwnerUnreadSignal.set(0);
    this.globalSubscriptionUnreadSignal.set(0);
    this.globalAdministrationUnreadSignal.set(0);
    this.globalModuleUnreadSignal.set({});
  }
}
